import React from 'react';
import PropTypes from 'prop-types';

import {Box, Typography, Paper, Divider} from 'views/components';
import {Translate, translate, useNavigate} from 'views/module';

import moment from 'moment';

const JobCard = (props) => {
  const {item} = props;

  const job = item;
  const navigation = useNavigate();

  const metrics = React.useMemo(() => {
    const dayMap = translate('career.jobCard.Days');

    let totalHours = 0;
    const scheduleChips = [];

    const hourlyRate = job.price?.hourlyRate || 0;

    if (job.schedule.type === 'RECURRENCE_EVENT' && job.schedule?.recurrenceRule?.weeklySlots?.length) {
      job.schedule.recurrenceRule.weeklySlots.forEach((slot) => {
        const startTime = new Date(slot.startTime);
        const endTime = new Date(slot.endTime);
        const hours = Math.round((endTime - startTime) / (1000 * 60 * 60) * 100) / 100;
        totalHours += hours;

        const startTimeStr = moment(startTime).format('h:mm A');
        const endTimeStr = moment(endTime).format('h:mm A');

        scheduleChips.push({
          day: dayMap[slot.day],
          timeRange: `${startTimeStr}-${endTimeStr}`,
          label: translate('career.jobCard.ScheduleLabel', {
            day: dayMap[slot.day],
            timeRange: `${startTimeStr}-${endTimeStr}`,
          }),
        });
      });

      const weeklyPay = totalHours * hourlyRate;
      const shiftCount = job.schedule.recurrenceRule.weeklySlots.length;

      return {
        totalHours,
        shiftCount,
        weeklyPay,
        scheduleChips,
      };
    }

    if (job.schedule.type === 'LIVEIN_EVENT') {
      const paidShiftHours = 8;
      const shiftsPerWeek = 7;
      const paidHoursPerWeek = paidShiftHours * shiftsPerWeek;
      const weeklyPay = paidHoursPerWeek * hourlyRate;

      scheduleChips.push({
        day: '',
        timeRange: '',
        label: translate('career.jobCard.LiveIn'),
      });

      return {
        totalHours: paidHoursPerWeek,
        shiftCount: shiftsPerWeek,
        weeklyPay,
        scheduleChips,
      };
    }

    // Default fallback
    return {totalHours: 0, shiftCount: 0, weeklyPay: 0, scheduleChips: []};
  }, [job]);

  const handleCardClick = () => {
    navigation(`/career/${job.jobRn}`);
  };

  return (
    <Paper
      sx={{
        'overflow': 'hidden',
        'p': 2,
        'bgcolor': '#ffffff',
        'width': '100%',
        'opacity': 1,
        'border': 'none',
        'cursor': 'pointer',
        '&:hover': {
          backgroundColor: '#f9f9f9',
        },
      }}
      onClick={handleCardClick}
    >
      {/* Header: Location and Weekly Pay */}
      <Box
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <Box style={{display: 'flex', flexDirection: 'row', alignItems: 'center', flex: 1}}>
          {/* Status Chip */}
          <Box
            style={{
              backgroundColor: job.status === 'ACTIVE' ? '#e8f5e9' : '#9e9e9e',
              borderColor: job.status === 'ACTIVE' ? '#43a047' : '#757575',
              borderWidth: 1,
              borderRadius: 16,
              alignItems: 'center',
              display: 'inline-flex',
              padding: '4px 10px',
              marginRight: 10,
            }}
          >
            <Typography sx={{fontSize: 12, color: job.status === 'ACTIVE' ? '#388e3c' : '#fff', fontWeight: 500}}>
              {job.status}
            </Typography>
          </Box>

          <Typography variant="h5" component="div" style={{marginRight: 12}}>
            <Translate
              expression={'career.jobCard.Location'}
              substitute={{
                clientName: job?.client?.clientName || translate('career.jobCard.ClientUnknown'),
                neighborhood: job.location.neighborhood,
                city: job.location.city,
              }}
            />
          </Typography>

          {/* Applicants Chip */}
          <Box
            style={{
              backgroundColor: '#e3f2fd',
              borderColor: '#1976d2',
              borderWidth: 1,
              borderRadius: 16,
              alignItems: 'center',
              display: 'inline-flex',
              padding: '4px 8px',
            }}
          >
            <Typography sx={{fontSize: 12, color: '#1976d2', fontWeight: 500}}>
              <Translate
                expression={'career.jobCard.Applications'}
                substitute={{
                  count: job.applications.length,
                }}
              />
            </Typography>
          </Box>
          {
            job?.externalApplications?.length > 0 &&
            <Box
              style={{
                backgroundColor: '#e3f2fd',
                borderColor: '#1976d2',
                borderWidth: 1,
                borderRadius: 16,
                alignItems: 'center',
                display: 'inline-flex',
                padding: '4px 8px',
              }}
            >
              <Typography sx={{fontSize: 12, color: '#1976d2', fontWeight: 500}}>
                <Translate
                  expression={'career.jobCard.External'}
                  substitute={{
                    count: job?.externalApplications.length,
                  }}
                />
              </Typography>
            </Box>
          }
        </Box>
      </Box>

      {/* Hourly Rate, Hours per Week, Shift Count */}
      <Typography sx={{fontSize: 16}} color="text.secondary">
        <Translate
          expression={'career.jobCard.JobDetails'}
          substitute={{
            amount: metrics.weeklyPay,
            rate: job.price.hourlyRate,
            hours: metrics.totalHours,
            count: metrics.shiftCount,
          }}
        />
      </Typography>

      <Divider />


      {/* Short Description */}
      <Typography sx={{fontSize: 12, marginBottom: 2}} color="text.secondary">
        {job.description.short}
      </Typography>

      {/* Schedule Chips */}
      <Box
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'center',
          marginBottom: 4,
          alignItems: 'flex-start',
        }}
      >
        {
          metrics.scheduleChips.map((chip, index) => (
            <Box
              key={index}
              style={{
                backgroundColor: '#f5f5f5',
                borderColor: '#e0e0e0',
                borderWidth: 1,
                borderRadius: 16,
                alignItems: 'center',
                marginRight: 12,
                display: 'inline-flex',
                padding: '4px 8px',
              }}
            >
              <Typography sx={{fontSize: 12}} color="text.secondary">
                {chip.label}
              </Typography>
            </Box>
          ))
        }
      </Box>
    </Paper>
  );
};

JobCard.propTypes = {
  item: PropTypes.shape({
    jobRn: PropTypes.string.isRequired,
    applications: PropTypes.array.isRequired,
    description: PropTypes.shape({
      short: PropTypes.string,
    }),
    location: PropTypes.shape({
      city: PropTypes.string,
      neighborhood: PropTypes.string,
    }),
    schedule: PropTypes.shape({
      type: PropTypes.oneOf(['RECURRENCE_EVENT', 'LIVEIN_EVENT']).isRequired,
      recurrenceRule: PropTypes.shape({
        weeklySlots: PropTypes.arrayOf(PropTypes.shape({
          day: PropTypes.string,
          startTime: PropTypes.string,
          endTime: PropTypes.string,
        })),
      }),
    }),
    price: PropTypes.shape({
      hourlyRate: PropTypes.number,
    }),
  }).isRequired,
  status: PropTypes.string,
};

export default JobCard;
